import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Tab } from 'semantic-ui-react';

import classNames from 'classnames';
import { v4 as uuidv4 } from 'uuid';

import useFeatureFlags from 'components/FeatureFlagsProvider/hooks/useFeatureFlags';
import useErrorNotification from 'hooks/useErrorNotification';
import { useGlossaryPageData } from './hooks/useGlossaryPageData';
import NoData from '../NoData';

import { GlossaryData, Vocabulary } from 'types/types';
import { t } from 'utils/resources';

import './GlossaryPage.less';

interface GlossaryPageProps {
  module: string;
}

export const GlossaryPage = ({ module }: GlossaryPageProps) => {
  const { featureFlags, isLoading } = useFeatureFlags();
  const { showErrorNotification } = useErrorNotification();

  const [activeAlphabet, setActiveAlphabet] = useState<string>();
  const [dataAlphabets, setDataAlphabets] = useState<string[]>([]);
  const [glossaryResponse, setGlossaryResponse] = useState<any[]>();
  const [glossaryData, setGlossaryData] = useState<Array<GlossaryData>>([]);
  const glossaryReferrences = useRef<any>([]);

  const { UseConfigModule: useConfigModule } = featureFlags;

  const glossaryPageApiDataRequestor = useGlossaryPageData();

  const scrollIntoView = useCallback((element: any) => {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, []);

  const executeScroll = useCallback(
    (alphabet: string) => {
      if (!glossaryReferrences?.current?.length) return;

      const alphabetElement = glossaryReferrences.current.find(
        (word: any) => word.id === `alphabet-${alphabet}`
      );

      if (alphabetElement) scrollIntoView(alphabetElement);
    },
    [scrollIntoView]
  );

  const genAlphabetsArray = useMemo(
    () => Array.from({ length: 26 }, (_, i) => String.fromCharCode('A'.charCodeAt(0) + i)),
    []
  );

  const extractAlphabet = (word: string) => word.charAt(0);

  const handleGlossaryBanner = useCallback((vocabularyData: any) => {
    const alphabets = Array.from(
      new Set(vocabularyData.map(({ word }: any) => extractAlphabet(word)))
    );
    const sortedAlphabets = (alphabets as string[]).sort((a: string, b: string) =>
      a.localeCompare(b)
    );

    const glossaryPreparedData = sortedAlphabets.map((alphabet) => {
      const vocabulary = vocabularyData
        .filter((data: any) => extractAlphabet(data.word) === alphabet)
        .sort((a: any, b: any) => a.word.localeCompare(b.word));

      return {
        alphabet,
        vocabulary,
      } as GlossaryData;
    });

    setDataAlphabets(sortedAlphabets);
    setGlossaryData(glossaryPreparedData);
  }, []);

  const handleAlphabetClick = useCallback(
    (alphabet: string) => () => {
      if (alphabet) setActiveAlphabet(alphabet);

      executeScroll(alphabet);
    },
    [executeScroll]
  );

  const renderAlphabetsBanner = useMemo(() => {
    return genAlphabetsArray.map((alphabet: string) => (
      <div
        key={`alphabet-${uuidv4()}`}
        className={classNames({
          'alphabet-banner-alphabet': true,
          'alphabet-banner-active-alphabet': activeAlphabet && activeAlphabet === alphabet,
          'alphabet-banner-disable-alphabet': !dataAlphabets.includes(alphabet),
        })}
        data-testid={`alphabet-banner-${alphabet}`}
        onClick={handleAlphabetClick(alphabet)}
        onKeyDown={handleAlphabetClick(alphabet)}
        role="menuitem"
        tabIndex={0}
      >
        {alphabet}
      </div>
    ));
  }, [activeAlphabet, dataAlphabets, genAlphabetsArray, handleAlphabetClick]);

  const filterGlossaryData = (data: any, moduleValue: any) => {
    return data.filter(
      (item: any) => item.module && (item.module === moduleValue || item.module === '*')
    );
  };

  const handleGlossaryData = useCallback(
    (vocabularyData: any): void => {
      if (!module || !vocabularyData || vocabularyData.length === 0) return;

      const filteredGlossaryData = filterGlossaryData(vocabularyData, module);

      handleGlossaryBanner(filteredGlossaryData);
    },
    [module, handleGlossaryBanner]
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const glossaryDataReponse = await glossaryPageApiDataRequestor(useConfigModule);

        setGlossaryResponse(glossaryDataReponse);
      } catch (error) {
        showErrorNotification({
          errorTitle: t('HelpSectionErrorTitle'),
          content: t('PageInfoErrorMessage'),
        });
      }
    };

    fetchData();
  }, [glossaryPageApiDataRequestor, useConfigModule, showErrorNotification]);

  useEffect(() => {
    if (glossaryResponse) handleGlossaryData(glossaryResponse);
  }, [glossaryResponse, handleGlossaryData]);

  if (isLoading || !glossaryData?.length) return <NoData />;

  return (
    <Tab.Pane>
      <div className="tab-container">
        <div className="glossary-container" id="glossary-container">
          <p>{t('HelpSection.Glossary.Header')}</p>
          <div className="alphabet-banner">
            <div className="alphabet-banner-section">{renderAlphabetsBanner}</div>
          </div>
          <div className="glossary-section">
            {glossaryData.map((data: GlossaryData, index: number) => (
              <div
                key={`alphabet-data-${uuidv4()}`}
                className="glossary-section-alphabet-group"
                id={`alphabet-${data.alphabet}`}
                data-testid={`alphabet-group-${data.alphabet}`}
                ref={(el) => (glossaryReferrences.current[index] = el)}
              >
                <div className="glossary-section-alphabet-group-alphabet">
                  <span>{data.alphabet}</span>
                </div>
                <div className="glossary-section-alphabet-group-vocabulary">
                  {data.vocabulary.map((vocabulary: Vocabulary) => (
                    <div
                      key={`word-${uuidv4()}`}
                      className="glossary-section-alphabet-group-vocabulary-word"
                    >
                      <p>
                        <b>{vocabulary.word}:</b> {vocabulary.meaning}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Tab.Pane>
  );
};
